import React, { useEffect, useMemo, useRef, useState } from "react"
import ProductScrollableList from "../ProductScrollableList"
import { useCategories } from "../../hooks/useCatalogData"
import useSiteLanguage from "../../hooks/useSiteLanguage"
import { buildCategoryUrl, collectCategoryIds, hasProducts, isRootCategory } from "../../utils/categories"

/**
 * /categories with no category chosen: a row of the newest products of each
 * root category (its whole subtree), in the side column's order. The rows
 * load one after another, and only as the visitor scrolls near them: the next
 * asks once the one above has its answer and the end of the list is close to
 * the screen. A row is a request, the categories are dozens, and the API turns
 * away a visitor who asks too much at once (a turned-away row shows nothing).
 */
export default function CategoryLatestRows({ placement = "category" }) {
  const language = useSiteLanguage()
  const { list, loaded } = useCategories()
  // a language switch reads the categories again: until they are here there
  // is nothing to list, and then the rows start over from the first, one by
  // one, rather than every row the old language had reached asking at once
  if (!loaded) return null
  return <Rows key={language} list={list} placement={placement} />
}

// the shop's own lists kept as categories (the home page's rows): no shelf of
// their own here — a "Latest" row on the page of the latest says nothing
const LISTS = ["featured", "latest", "popular"]

// how far below the screen the next row starts loading
const AHEAD = "600px"

function Rows({ list, placement }) {
  const roots = useMemo(() => list.filter((c) => isRootCategory(c) && hasProducts(c) && !LISTS.includes(c.categoryId)), [list])
  // rows started, rows that have their answer, and whether the end of the
  // list is near the screen: one more row when the last has settled and the end is near
  const [started, setStarted] = useState(1)
  const [settled, setSettled] = useState(0)
  const [near, setNear] = useState(false)
  const end = useRef(null)

  useEffect(() => {
    const el = end.current
    if (!el || typeof IntersectionObserver === "undefined") return setNear(true)
    const observer = new IntersectionObserver(([entry]) => setNear(entry.isIntersecting), { rootMargin: `0px 0px ${AHEAD} 0px` })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (near && settled >= started && started < roots.length) setStarted(started + 1)
  }, [near, settled, started, roots.length])

  return (
    <div className="w-full flex flex-col gap-10 mt-6">
      {roots.slice(0, started).map((cat, index) => (
        <ProductScrollableList
          key={cat.categoryId}
          category={cat.categoryId}
          categories={collectCategoryIds(cat.categoryId, list)}
          seeMoreHref={buildCategoryUrl(cat.categoryId, list)}
          sortBy="published"
          itemsPerPage={10}
          placement={placement}
          onSettled={() => setSettled((n) => Math.max(n, index + 1))}
        />
      ))}
      {/* the end of the list: near the screen, the next row starts */}
      <div ref={end} className="h-px -mt-10" aria-hidden />
    </div>
  )
}
